import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useServiceModuleById } from "../../../store/selectors/serviceModulesSelector";
import {  useCallback, useEffect, useMemo, useRef, useState } from "react";
import { serviceModulesApi } from "../../../api/serviceModulesApi";
import { PersonalDocumentEmployeeResponse } from "../../../types/personalDocuments";
import { PersonalDocumentsDialogEmployee } from "./PersonalDocumentsDialogEmployee";
import { PersonalDocumentsDialogUpload } from "./PersonalDocumentsDialogUpload";
import { PersonalDocumentsDialogLatest } from "./PersonalDocumentsDialogLatest";
import PageTitle from "../../../components/common/PageTitle/PageTitle";
import { Box, Card, CardContent, Grid, IconButton, Stack, Tooltip, Typography, alpha, TextField, InputAdornment, Popover, Link, Button, CircularProgress, Paper } from "@mui/material";
import { Visibility, CloudUpload, Search, FileUpload, InfoOutlined } from "@mui/icons-material";
import { theme } from "../../../theme";

export const PersonalDocumentsPage = () => {
  const { t } = useTranslation();
  const { serviceModuleId } = useParams<{ serviceModuleId: string }>();
  const parsedServiceModuleId = Number(serviceModuleId);
  const lookupServiceModuleId = Number.isFinite(parsedServiceModuleId) ? parsedServiceModuleId : -1;
  const serviceModule = useServiceModuleById(lookupServiceModuleId);

  const [employees, setEmployees] = useState<PersonalDocumentEmployeeResponse[] | undefined>([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [isDraggingGlobal, setIsDraggingGlobal] = useState(false);
  const [draggingEmployeeId, setDraggingEmployeeId] = useState<string | null>(null);
  
  const globalFileInputRef = useRef<HTMLInputElement>(null);
  const employeeFileInputRef = useRef<HTMLInputElement>(null);
  const [activeEmployeeId, setActiveEmployeeId] = useState<string | null>(null);

  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLButtonElement | null>(null);

  const [selectedEmployee, setSelectedEmployee] = useState<PersonalDocumentEmployeeResponse | null>(null);
  const [isEmployeeDialogOpen, setIsEmployeeDialogOpen] = useState(false);

  const [isLatestDialogOpen, setIsLatestDialogOpen] = useState(false);

  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadTargetEmployee, setUploadTargetEmployee] = useState<PersonalDocumentEmployeeResponse | null>(null);

  const loadEmployees = useCallback(async (showLoader = true) => {
    if (showLoader) setIsLoadingEmployees(true);
    const response = await serviceModulesApi.getPersonalDocumentEmployees(Number(serviceModuleId));
    if (response.success() && response.result) {
      setEmployees(response.result);
    }
    else {
      setEmployees(undefined);
    }
    if (showLoader) setIsLoadingEmployees(false);
  }, [serviceModuleId]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const filteredEmployees = useMemo(() => {
    if (employees === undefined) return undefined

    return employees?.filter(employee =>
      `${employee.firstName} ${employee.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [employees, searchQuery]);

  const handleUploadFiles = useCallback((files: FileList | null, employeeId?: string) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    setSelectedFiles(fileArray);
    
    if (employeeId) {
      const employee = employees?.find(e => e.employeeId === employeeId);
      setUploadTargetEmployee(employee || null);
    } else {
      setUploadTargetEmployee(null);
    }
    
    setIsUploadDialogOpen(true);
  }, [employees]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnterGlobal = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingGlobal(true);
  }

  const handleDragLeaveGlobal = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node)) {
      return;
    }
    setIsDraggingGlobal(false);
  }

  const handleDropGlobal = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingGlobal(false);
    handleUploadFiles(e.dataTransfer.files);
  }

  const handleSelectDoumentsGlobal = () => {
    globalFileInputRef.current?.click();
  }

  const handleDragEnterEmployee = (e: React.DragEvent, employee: PersonalDocumentEmployeeResponse) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggingEmployeeId(employee.employeeId);
  }

  const handleDragLeaveEmployee = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node)) {
      return;
    }
    setDraggingEmployeeId(null);
  }

  const handleDropEmployee = (e: React.DragEvent, employee: PersonalDocumentEmployeeResponse) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggingEmployeeId(null);
    handleUploadFiles(e.dataTransfer.files, employee.employeeId);
  }

  const handleViewEmployee = (employee: PersonalDocumentEmployeeResponse) => {
    setSelectedEmployee(employee);
    setIsEmployeeDialogOpen(true);
  }

  const handleSelectDocumentsEmployee = (employee: PersonalDocumentEmployeeResponse) => {
    setActiveEmployeeId(employee.employeeId);
    employeeFileInputRef.current?.click();
  }

  const handleOpenInfo = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setInfoAnchorEl(event.currentTarget);
  };

  const handleCloseInfo = (event: React.MouseEvent) => {
    event.stopPropagation();
    setInfoAnchorEl(null);
  };

  const pageTitle = serviceModule?.name ?? t("serviceModulesPage.personalDocumentsPage.title");

  const breadcrumbs = useMemo(() => {
    const crumbs = [
      {
        title: "serviceModulesPage.serviceModules",
        link: "serviceModules",
      },
    ];

    if (serviceModuleId) {
      crumbs.push({
        title: pageTitle,
        link: `serviceModules/personaldocuments/${serviceModuleId}`,
      });
    }
    return crumbs;
  }, [pageTitle, serviceModuleId]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3}}>
      <PageTitle
        breadcrumbs={breadcrumbs}
        title={pageTitle}
      />

      <input
        type="file"
        accept="*,application/pdf"
        multiple
        ref={globalFileInputRef}
        style={{ display: "none" }}
        onChange={(e) => {
          handleUploadFiles(e.target.files);
          e.target.value = '';
        }}
      />

      <input
        type="file"
        accept="*,application/pdf"
        multiple
        ref={employeeFileInputRef}
        style={{ display: "none" }}
        onChange={(e) => {
          if (activeEmployeeId) {
            handleUploadFiles(e.target.files, activeEmployeeId);
            setActiveEmployeeId(null);
          }
          e.target.value = '';
        }}
      />

      <GlobalDocumentsCard
        isDraggingGlobal={isDraggingGlobal}
        handleDragOver={handleDragOver}
        handleDragEnterGlobal={handleDragEnterGlobal}
        handleDragLeaveGlobal={handleDragLeaveGlobal}
        handleDropGlobal={handleDropGlobal}
        handleOpenInfo={handleOpenInfo}
        handleSelectDocuments={handleSelectDoumentsGlobal}
      />

      <Popover
        open={!!infoAnchorEl}
        anchorEl={infoAnchorEl}
        onClose={handleCloseInfo}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{padding: 2}}>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold" }} color="primary" gutterBottom>
            {t("serviceModulesPage.personalDocumentsPage.uploadFilesForEmployeesPopover.title")}
          </Typography>
          <Typography variant="body2" gutterBottom>
            {t("serviceModulesPage.personalDocumentsPage.uploadFilesForEmployeesPopover.format")}
          </Typography>
          <Typography variant="body2" gutterBottom>
            {t("serviceModulesPage.personalDocumentsPage.uploadFilesForEmployeesPopover.example")}
          </Typography>
          <Typography variant="body2" gutterBottom>
            {t("serviceModulesPage.personalDocumentsPage.uploadFilesForEmployeesPopover.assignment")}
          </Typography>
        </Box>
      </Popover>

      <Paper sx={{ padding: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6">{t("serviceModulesPage.personalDocumentsPage.listEmployees")}</Typography>
          <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Visibility fontSize="small" />}
              onClick={() => setIsLatestDialogOpen(true)}
              sx={{ width: "300px", height: "40px" }}
            >
              {t("serviceModulesPage.personalDocumentsPage.recentlyUploaded")}
            </Button>
            <TextField
              size="small"
              placeholder={t("serviceModulesPage.personalDocumentsPage.searchLabel")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ width: "300px" }}
            />
          </Stack>
        </Box>

        <EmployeeDocumentsCard
          isLoadingEmployees={isLoadingEmployees}
          filteredEmployees={filteredEmployees}
          draggingEmployeeId={draggingEmployeeId}
          handleDragOver={handleDragOver}
          handleDragEnter={handleDragEnterEmployee}
          handleDragLeave={handleDragLeaveEmployee}
          handleDrop={handleDropEmployee}
          handleView={handleViewEmployee}
          handleSelectDocuments={handleSelectDocumentsEmployee}
        />
      </Paper>

      <PersonalDocumentsDialogEmployee
        open={isEmployeeDialogOpen}
        onClose={() => {
          setIsEmployeeDialogOpen(false);
          setSelectedEmployee(null);
          loadEmployees(false);
        }}
        serviceModuleId={parsedServiceModuleId}
        employee={selectedEmployee}
      />

      <PersonalDocumentsDialogUpload
        open={isUploadDialogOpen}
        onClose={() => {
          setIsUploadDialogOpen(false);
          setSelectedFiles([]);
          setUploadTargetEmployee(null);
          loadEmployees(false);
        }}
        serviceModuleId={parsedServiceModuleId}
        initialFiles={selectedFiles}
        employee={uploadTargetEmployee}
      />

      <PersonalDocumentsDialogLatest
        open={isLatestDialogOpen}
        onClose={() => {
          setIsLatestDialogOpen(false);
          loadEmployees(false);
        }}
        serviceModuleId={parsedServiceModuleId}
      />
    </Box>
  );
};

interface GlobalDocumentsCardProps {
  isDraggingGlobal: boolean
  handleDragOver: (e: React.DragEvent) => void;
  handleDragEnterGlobal: (e: React.DragEvent) => void;
  handleDragLeaveGlobal: (e: React.DragEvent) => void;
  handleDropGlobal: (e: React.DragEvent) => void;
  handleOpenInfo: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleSelectDocuments: () => void;
}

const GlobalDocumentsCard: React.FC<GlobalDocumentsCardProps> = ({
  isDraggingGlobal,
  handleDragOver,
  handleDragEnterGlobal,
  handleDragLeaveGlobal,
  handleDropGlobal,
  handleOpenInfo,
  handleSelectDocuments,
}) => {
  const { t } = useTranslation();
  return (
    <Card
      sx={{
        p: 3,
        textAlign: "center",
        position: 'relative',
        border: isDraggingGlobal ? `1px dashed ${theme.palette.primary.main}` : `1px dashed ${theme.palette.divider}`,
        backgroundColor: isDraggingGlobal ? alpha(theme.palette.primary.main, 0.08) : "background.paper",
      }}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnterGlobal}
      onDragLeave={handleDragLeaveGlobal}
      onDrop={handleDropGlobal}
    >
      <Stack
        spacing={1}
        sx={{
          alignItems: "center",
          position: 'relative',
          pointerEvents: isDraggingGlobal ? 'none' : 'auto'
        }}
      >
        <CloudUpload sx={{ fontSize: 48, color: theme.palette.primary.main }} />
        <Typography variant="h6">
          {t("serviceModulesPage.personalDocumentsPage.uploadFilesForEmployees")}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
          <Tooltip title={t("serviceModulesPage.personalDocumentsPage.uploadFilesForEmployeesTooltip")}>
            <IconButton
              onClick={handleOpenInfo}
              size="small"
              color="primary"
            >
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="caption" sx={{ lineHeight: 1 }}>
            {t("serviceModulesPage.personalDocumentsPage.uploadFilesForEmployeesInfo")}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} sx={{ justifyContent: "center", alignItems: "center" }}>
            <FileUpload fontSize="small" color="primary" />
            <Typography 
              variant="caption" 
              sx={{ 
                fontWeight: 600, 
                color: "primary.main",
                display: 'inline-flex',
                alignItems: 'center'
              }}
            >
              Drag & Drop {t("serviceModulesPage.personalDocumentsPage.or")}&nbsp;
              <Link
                onClick={handleSelectDocuments}
                sx={{cursor: "pointer"}}
              >
                {t("serviceModulesPage.personalDocumentsPage.select")}
              </Link>
            </Typography>
          </Stack>
      </Stack>
    </Card>
  );
}

interface EmployeeDocumentsCardProps {
  isLoadingEmployees: boolean;
  filteredEmployees: PersonalDocumentEmployeeResponse[] | undefined;
  draggingEmployeeId: string | null;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragEnter: (e: React.DragEvent, employee: PersonalDocumentEmployeeResponse) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent, employee: PersonalDocumentEmployeeResponse) => void;
  handleView: (employee: PersonalDocumentEmployeeResponse) => void;
  handleSelectDocuments: (employee: PersonalDocumentEmployeeResponse) => void;
}

const EmployeeDocumentsCard: React.FC<EmployeeDocumentsCardProps> = ({
  isLoadingEmployees,
  filteredEmployees,
  draggingEmployeeId,
  handleDragOver,
  handleDragEnter,
  handleDragLeave,
  handleDrop,
  handleView,
  handleSelectDocuments
}) => {
  const { t } = useTranslation();

  if (isLoadingEmployees) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (filteredEmployees === undefined) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography color="textSecondary">{t("serviceModulesPage.personalDocumentsPage.shared.failedLoading")}</Typography>
      </Box>
    );
  }

  if (filteredEmployees.length === 0) {
    return (
       <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography color="textSecondary">{t("serviceModulesPage.personalDocumentsPage.listEmployeesEmpty")}</Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {filteredEmployees.map((employee) => (
        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={employee.employeeId}>
          <Card
            sx={{
              height: "100%",
              position: 'relative',
              border: draggingEmployeeId === employee.employeeId ? `1px dashed ${theme.palette.primary.main}` : `1px dashed ${theme.palette.divider}`,
              backgroundColor: draggingEmployeeId === employee.employeeId ? alpha(theme.palette.primary.main, 0.08) : "background.paper",
              zIndex: draggingEmployeeId === employee.employeeId ? 1 : 0,
              '&:hover': {
                boxShadow: theme.shadows[2],
              }
            }}
            onDragOver={handleDragOver}
            onDragEnter={(e) => handleDragEnter(e, employee)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, employee)}
          >
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Stack direction="column" spacing={1} sx={{ alignItems: "flex-start" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {employee.firstName} {employee.lastName}
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                  {t("serviceModulesPage.personalDocumentsPage.documentsTotalcount")}: {employee.documentCount}
                </Typography>

                </Stack>
                <Tooltip title={t("buttons.show")}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleView(employee);
                    }}
                  >
                    <Visibility fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              
              <Stack direction="row" spacing={1} sx={{ justifyContent: "center", alignItems: "center" }}>
                <FileUpload fontSize="small" color="primary" />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontWeight: 600, 
                    color: "primary.main",
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}
                >
                  Drag & Drop {t("serviceModulesPage.personalDocumentsPage.or")}&nbsp;
                  <Link 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectDocuments(employee);
                    }}
                    sx={{cursor: "pointer"}}
                  >
                    {t("serviceModulesPage.personalDocumentsPage.select")}
                  </Link>
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}